import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { api } from '@mmef/shared/api';
import { selectCurrentUser, selectIsAdmin } from '@mmef/shared/auth';
import { useSignOutMutation } from '@mmef/shared/auth';

/** Empty unless the deployment exposes an admin panel the public site can link to. */
const ADMIN_URL = import.meta.env.VITE_ADMIN_URL ?? '';

/**
 * Initials for the avatar. A set name gives its first and last initial; otherwise the email's
 * first letter, which is at least stable and recognisable.
 */
function initialsOf(displayName: string | null, email: string): string {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
  }
  return email[0]?.toUpperCase() ?? '?';
}

/**
 * Avatar button that opens the account menu.
 *
 * <p>Keeps the nav short: every account destination lives in here rather than competing for space
 * in the bar, which is what let Alerts, Organisers, Moderation and Analytics pile up before.
 *
 * <p>The fiddly parts of a dropdown, all of which are easy to leave out and annoying to hit:
 * closing on Escape, closing on a click elsewhere, and returning focus to the button afterwards so
 * keyboard users aren't dumped at the top of the document.
 */
export function AccountMenu() {
  const user = useAppSelector(selectCurrentUser);
  const isAdmin = useAppSelector(selectIsAdmin);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [signOut] = useSignOutMutation();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus(); // don't strand keyboard focus at the page root
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) return null;

  const label = user.displayName?.trim() || user.email;

  const onSignOut = async () => {
    setOpen(false);
    try {
      await signOut().unwrap();
    } finally {
      // Wipe cached responses — some of it is this person's data, and the next person at this
      // browser shouldn't see it flash up before a refetch replaces it.
      dispatch(api.util.resetApiState());
      navigate('/');
    }
  };

  return (
    <div className="account-menu" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className="account-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className="avatar" aria-hidden="true">
          {initialsOf(user.displayName, user.email)}
        </span>
        <span className="account-menu__name">{label}</span>
        <span className="account-menu__chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="account-menu__panel" role="menu">
          {/* The full address, because the button may be showing a name instead and people do
              forget which account they're in. */}
          <p className="account-menu__email">{user.email}</p>

          <Link role="menuitem" to="/account" onClick={() => setOpen(false)}>
            Your account
          </Link>
          <Link role="menuitem" to="/account/alerts" onClick={() => setOpen(false)}>
            Your alerts
          </Link>
          <Link role="menuitem" to="/organisers" onClick={() => setOpen(false)}>
            Organiser accounts
          </Link>

          {/* The admin panel is a separate origin now, so this is an absolute link rather than a
              route — and it only appears when one is configured, since a deployment may keep the
              panel on a private network with no address the public site could sensibly point at. */}
          {isAdmin && ADMIN_URL && (
            <>
              <hr />
              <a role="menuitem" href={ADMIN_URL} onClick={() => setOpen(false)}>
                Admin panel
              </a>
            </>
          )}

          <hr />
          <button role="menuitem" type="button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
